import { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import {
  $createLineBreakNode, $createParagraphNode, $createTextNode, $getRoot,
  $getSelection, $isElementNode, $isRangeSelection, $isTextNode,
  COMMAND_PRIORITY_CRITICAL, DROP_COMMAND, KEY_DOWN_COMMAND, PASTE_COMMAND,
} from 'lexical';
import { HashtagNode } from '@lexical/hashtag';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { HashtagPlugin } from '@lexical/react/LexicalHashtagPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';

// One paragraph with explicit line breaks keeps offsets identical to Mastodon's
// plain-text API (including emoji UTF-16 offsets and trailing newlines).
export function $setComposerText(text) {
  const paragraph = $createParagraphNode();
  text.split('\n').forEach((line, index) => {
    if (index) paragraph.append($createLineBreakNode());
    if (line) paragraph.append($createTextNode(line));
  });
  $getRoot().clear().append(paragraph);
}

function $pointOffset(point) {
  const target = point.getNode();
  let offset = 0;
  let found = false;
  const visit = node => {
    if (found) return;
    if (node.is(target)) {
      offset += $isElementNode(node)
        ? node.getChildren().slice(0, point.offset).reduce((sum, child) => sum + child.getTextContentSize(), 0)
        : point.offset;
      found = true;
    } else if ($isElementNode(node)) node.getChildren().forEach(visit);
    else offset += node.getTextContentSize();
  };
  visit($getRoot());
  return offset;
}

export function $composerSelection() {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return null;
  const anchor = $pointOffset(selection.anchor), focus = $pointOffset(selection.focus);
  return { start: Math.min(anchor, focus), end: Math.max(anchor, focus) };
}

function $position(offset) {
  let remaining = Math.max(0, Math.min(offset, $getRoot().getTextContentSize()));
  const paragraph = $getRoot().getFirstChild();
  const nodes = paragraph.getChildren();
  for (let index = 0; index < nodes.length; index++) {
    const node = nodes[index], size = node.getTextContentSize();
    if ($isTextNode(node) && remaining <= size) return [node.getKey(), remaining, 'text'];
    if (remaining === 0) return [paragraph.getKey(), index, 'element'];
    remaining -= size;
  }
  return [paragraph.getKey(), nodes.length, 'element'];
}

export function $selectComposerRange(start, end) {
  const paragraph = $getRoot().getFirstChild();
  const selection = paragraph.select(0, 0);
  selection.anchor.set(...$position(start));
  selection.focus.set(...$position(end));
}

function ComposerBridge({ props, forwardedRef }) {
  const [editor] = useLexicalComposerContext();
  const latest = useRef(props);
  latest.current = props;
  const text = useRef(props.value ?? '');
  const selection = useRef({ start: text.current.length, end: text.current.length });

  useLayoutEffect(() => {
    const removeRootListener = editor.registerRootListener(element => {
      if (element) {
        // Preserve the native-input-shaped contract used by Mastodon's compose
        // actions, without a hidden textarea or any duplicate text surface.
        Object.defineProperties(element, {
          value: { configurable: true, get: () => text.current },
          selectionStart: { configurable: true, get: () => selection.current.start },
          selectionEnd: { configurable: true, get: () => selection.current.end },
          setSelectionRange: { configurable: true, value: (start, end) => {
            selection.current = { start, end };
            editor.update(() => $selectComposerRange(start, end), { discrete: true });
          } },
        });
      }
      if (typeof forwardedRef === 'function') forwardedRef(element);
      else if (forwardedRef) forwardedRef.current = element;
    });
    const removeUpdateListener = editor.registerUpdateListener(({ editorState, tags }) => {
      let value;
      editorState.read(() => {
        value = $getRoot().getTextContent();
        const currentSelection = $composerSelection();
        if (currentSelection) selection.current = currentSelection;
      });
      const element = editor.getRootElement();
      // A hashtag being edited stays ordinary text; after leaving it, it is blue.
      const anchor = window.getSelection()?.anchorNode;
      element?.querySelectorAll('.dr-editor-hashtag').forEach(tag => {
        tag.classList.toggle('dr-editor-hashtag-editing', document.activeElement === element && !!anchor && tag.contains(anchor));
      });
      if (value !== text.current) {
        text.current = value;
        if (!tags.has('dr-external-value') && element) latest.current.onChange?.({ target: element, currentTarget: element });
      }
    });
    // Lexical must see Mastodon's autocomplete and Ctrl/Cmd+Enter shortcuts
    // before its own Enter/Tab handlers, not afterwards in React bubbling.
    const removeKeys = editor.registerCommand(KEY_DOWN_COMMAND, event => {
      latest.current.onKeyDown?.(event);
      return event.defaultPrevented;
    }, COMMAND_PRIORITY_CRITICAL);
    const removePaste = editor.registerCommand(PASTE_COMMAND, event => {
      latest.current.onPaste?.(event);
      return event.defaultPrevented;
    }, COMMAND_PRIORITY_CRITICAL);
    const removeDrop = editor.registerCommand(DROP_COMMAND, event => {
      latest.current.onDrop?.(event);
      return event.defaultPrevented;
    }, COMMAND_PRIORITY_CRITICAL);
    if (latest.current.autoFocus) editor.focus();
    return () => {
      removeRootListener(); removeUpdateListener(); removeKeys(); removePaste(); removeDrop();
      if (typeof forwardedRef === 'function') forwardedRef(null);
      else if (forwardedRef) forwardedRef.current = null;
    };
  }, [editor, forwardedRef]);

  useLayoutEffect(() => { editor.setEditable(!props.disabled); }, [editor, props.disabled]);
  useLayoutEffect(() => {
    const value = props.value ?? '';
    if (value === text.current) return;
    const range = selection.current;
    editor.update(() => {
      $setComposerText(value);
      if (document.activeElement === editor.getRootElement()) $selectComposerRange(Math.min(range.start, value.length), Math.min(range.end, value.length));
    }, { discrete: true, tag: 'dr-external-value' });
  }, [editor, props.value]);
  return null;
}

export const DriscollEditor = forwardRef((props, forwardedRef) => {
  const [config] = useState(() => ({
    namespace: 'DriscollComposer', nodes: [HashtagNode], editable: !props.disabled,
    theme: { paragraph: 'dr-editor-paragraph', hashtag: 'dr-editor-hashtag' },
    editorState: () => $setComposerText(props.value ?? ''),
    onError: error => { throw error; },
  }));
  return <LexicalComposer initialConfig={config}>
    <PlainTextPlugin ErrorBoundary={LexicalErrorBoundary} contentEditable={
      <ContentEditable className={`${props.className ?? ''} dr-editor-input`}
        aria-label={props['aria-label'] ?? props.placeholder ?? 'Post text'}
        aria-autocomplete={props['aria-autocomplete']} aria-placeholder={props.placeholder ?? ''}
        placeholder={<span className='dr-editor-placeholder'>{props.placeholder}</span>}
        dir={props.dir} lang={props.lang} spellCheck
        onFocus={props.onFocus} onBlur={props.onBlur} onKeyUp={props.onKeyUp} />
    } />
    <HistoryPlugin />
    <HashtagPlugin />
    <ComposerBridge props={props} forwardedRef={forwardedRef} />
  </LexicalComposer>;
});
